import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiUser, FiMail, FiPhone, FiLock, FiCheckCircle, FiAlertCircle, FiEdit2, FiSave, FiX } from 'react-icons/fi';
import { changePassword, getUserProfile, updateUserProfile, resendVerification, updateMyName } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { StateCitySelect } from '../../components/common';
import MobileVerificationModal from './MobileVerificationModal';
import styles from './ProfilePage.module.css';

const ProfilePage = () => {
  const { user, markEmailVerified, refreshUser, updateToken } = useAuth();
  const [showMobileVerifyModal, setShowMobileVerifyModal] = useState(false);

  // Password change
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwLoading, setPwLoading] = useState(false);

  // Name editing
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [nameLoading, setNameLoading] = useState(false);

  // Personal Details
  const [addrForm, setAddrForm] = useState({
    flatNo: '', buildingName: '', addressLine1: '', addressLine2: '', city: '', state: '', pincode: '', country: 'India',
    gender: '', dateOfBirth: '', notes: '',
  });
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [addrLoading, setAddrLoading] = useState(false);
  const [addrFetched, setAddrFetched] = useState(false);

  // Resend verification
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    getUserProfile()
      .catch(() => ({ data: {} }))
      .then((profileRes) => {
        setAddrForm((f) => ({ ...f, ...(profileRes.data || {}) }));
        setAddrFetched(true);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePwChange = (e) => setPwForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword) { toast.error('New passwords do not match'); return; }
    setPwLoading(true);
    try {
      const result = await changePassword({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      // The old token is now invalid (password change bumps passwordChangedAt server-side) —
      // store the new one immediately or the next API call logs the user out.
      if (result?.token) updateToken(result.token);
      toast.success('Password changed successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  const openNameEdit = () => { setNameValue(user?.name || ''); setIsEditingName(true); };
  const cancelNameEdit = () => setIsEditingName(false);

  const handleSaveName = async (e) => {
    e.preventDefault();
    if (!nameValue.trim()) { toast.error('Name cannot be empty'); return; }
    setNameLoading(true);
    try {
      await updateMyName(nameValue.trim());
      await refreshUser();
      toast.success('Name updated successfully');
      setIsEditingName(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update name');
    } finally {
      setNameLoading(false);
    }
  };

  const handleAddrChange = (e) => setAddrForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    setAddrLoading(true);
    try {
      await updateUserProfile(addrForm);
      toast.success('Details saved successfully');
      setIsEditingDetails(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save details');
    } finally {
      setAddrLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      await resendVerification();
      toast.success('Verification email sent! Please check your inbox.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resend');
    } finally {
      setResendLoading(false);
    }
  };

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  const roleLabel = { super_admin: 'Super Admin', derasar_admin: 'Derasar Admin', user: 'Member' }[user?.role] || user?.role;

  const memberSince = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })
    : '—';

  const formatAddress = (form) => {
    const parts = [
      form.flatNo ? `${form.flatNo}, ${form.buildingName || ''}`.trim() : form.buildingName,
      form.addressLine1,
      form.addressLine2,
      form.city,
      form.state,
      form.pincode,
      form.country !== 'India' ? form.country : ''
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '—';
  };

  return (
    <div className={styles.container}>
      <div className="page-header">
        <h2>My Profile</h2>
      </div>

      {/* Email verification banner */}
      {user && !user.isEmailVerified && (
        <div className={styles.unverifiedBanner}>
          <FiAlertCircle size={16} />
          <span>Your email is not verified. Some features may be restricted.</span>
          <button className={styles.resendBtn} onClick={handleResendVerification} disabled={resendLoading}>
            {resendLoading ? 'Sending…' : 'Resend verification'}
          </button>
        </div>
      )}

      {/* Profile info */}
      <div className={styles.profileCard}>
        <div className={styles.avatarRow}>
          <div className={styles.avatar}>{initials}</div>
          <div className={styles.avatarInfo}>
            {isEditingName ? (
              <form onSubmit={handleSaveName} className={styles.nameEditForm}>
                <input
                  className={styles.nameInput}
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  autoFocus
                  disabled={nameLoading}
                  maxLength={100}
                  placeholder="Your full name"
                />
                <div className={styles.nameEditActions}>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={nameLoading}>
                    <FiSave size={13} /> {nameLoading ? 'Saving…' : 'Save'}
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={cancelNameEdit} disabled={nameLoading}>
                    <FiX size={13} /> Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div className={styles.nameRow}>
                <div className={styles.userName}>{user?.name}</div>
                <button
                  className={styles.nameEditBtn}
                  onClick={openNameEdit}
                  title="Edit name"
                  type="button"
                >
                  <FiEdit2 size={13} />
                </button>
              </div>
            )}
            <div className={styles.userRole}>{roleLabel}</div>
          </div>
        </div>

        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}><FiMail size={10} /> Email</span>
            <span className={styles.infoValue}>
              {user?.email || '—'}
              {user?.isEmailVerified
                ? <span className={styles.verifiedBadge}><FiCheckCircle size={11} /> Verified</span>
                : <span className={styles.unverifiedBadge}><FiAlertCircle size={11} /> Not verified</span>
              }
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}><FiPhone size={10} /> Mobile</span>
            <span className={styles.infoValue}>
              {user?.phone || '—'}
              {user?.isMobileVerified ? (
                <span className={styles.verifiedBadge}><FiCheckCircle size={11} /> Verified</span>
              ) : (
                <>
                  <span className={styles.unverifiedBadge}><FiAlertCircle size={11} /> Not verified</span>
                  <button
                    className={styles.verifyBtn}
                    type="button"
                    onClick={() => setShowMobileVerifyModal(true)}
                  >
                    Verify
                  </button>
                </>
              )}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}><FiUser size={10} /> Role</span>
            <span className={styles.infoValue}>{roleLabel}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Member Since</span>
            <span className={styles.infoValue}>{memberSince}</span>
          </div>
          {user?.role !== 'super_admin' && (
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Derasars Joined</span>
              <span className={styles.infoValue}>
                {(user?.derasars || []).filter((d) => d.status === 'approved').length}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Personal Details & Address — admin sees this in My Derasar tab instead */}
      {user?.role !== 'derasar_admin' && <div className={styles.profileCard}>
        <div className="page-header" style={{ marginBottom: 12 }}>
          <div className={styles.sectionTitle} style={{ margin: 0 }}>
            <FiUser size={15} style={{ marginRight: 6 }} /> {user?.role === 'derasar_admin' ? 'Derasar & Address Details' : 'Personal Details'}
          </div>
          {addrFetched && !isEditingDetails && (
            <button className="btn btn-ghost btn-sm" onClick={() => setIsEditingDetails(true)}>
              <FiEdit2 size={14} /> Edit Details
            </button>
          )}
        </div>

        {addrFetched ? (
          isEditingDetails ? (
            <form onSubmit={handleSaveAddress}>
              <p className={styles.sectionNote}>Optional — used for records only.</p>
              <div className={styles.addrGrid}>
                {user?.role === 'derasar_admin' ? (
                  <>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label>Establish Date</label>
                      <input type="date" name="establishDate" value={addrForm.establishDate || ''} onChange={handleAddrChange} />
                    </div>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label>Admin Notes</label>
                      <textarea 
                        name="derasarNotes" 
                        value={addrForm.derasarNotes || ''} 
                        onChange={handleAddrChange} 
                        placeholder="Internal notes for this Derasar…"
                        rows={2}
                      />
                    </div>
                    <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--color-border)', margin: '12px 0' }} />
                  </>
                ) : (
                  <>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label>Gender</label>
                      <select name="gender" value={addrForm.gender || ''} onChange={handleAddrChange}>
                        <option value="">Prefer not to say</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label>Date of Birth</label>
                      <input type="date" name="dateOfBirth" value={addrForm.dateOfBirth || ''} onChange={handleAddrChange} />
                    </div>
                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                      <label>Notes</label>
                      <textarea 
                        name="notes" 
                        value={addrForm.notes || ''} 
                        onChange={handleAddrChange} 
                        placeholder="Any additional personal notes…"
                        rows={2}
                      />
                    </div>
                    <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--color-border)', margin: '12px 0' }} />
                  </>
                )}

                <div className="form-group">
                  <label>Flat / Bunglow No.</label>
                  <input name="flatNo" value={addrForm.flatNo || ''} onChange={handleAddrChange} placeholder="e.g. A-101" />
                </div>
                <div className="form-group">
                  <label>Apartment / Bunglow Name</label>
                  <input name="buildingName" value={addrForm.buildingName || ''} onChange={handleAddrChange} placeholder="e.g. Shanti Niketan" />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Address Line 1</label>
                  <input name="addressLine1" value={addrForm.addressLine1} onChange={handleAddrChange} placeholder="Street / Area / Locality" />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Address Line 2</label>
                  <input name="addressLine2" value={addrForm.addressLine2} onChange={handleAddrChange} placeholder="Street / Area / Locality" />
                </div>
                <StateCitySelect
                  state={addrForm.state}
                  city={addrForm.city}
                  onStateChange={(val) => setAddrForm((f) => ({ ...f, state: val }))}
                  onCityChange={(val) => setAddrForm((f) => ({ ...f, city: val }))}
                />
                <div className="form-group">
                  <label>Pincode</label>
                  <input name="pincode" value={addrForm.pincode} onChange={handleAddrChange} placeholder="e.g. 380001" />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <input name="country" value={addrForm.country} onChange={handleAddrChange} placeholder="India" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" className="btn btn-primary" disabled={addrLoading}>
                  {addrLoading ? 'Saving…' : 'Save Details'}
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => {
                  // Optionally re-fetch to reset form, or just close:
                  setIsEditingDetails(false);
                }}>
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <div className={styles.infoGrid} style={{ marginTop: 12 }}>
              {user?.role === 'derasar_admin' ? (
                <>
                  <div className={styles.infoItem} style={{ gridColumn: '1 / -1' }}>
                    <span className={styles.infoLabel}>Establish Date</span>
                    <span className={styles.infoValue}>
                      {addrForm.establishDate ? new Date(addrForm.establishDate + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : '—'}
                    </span>
                  </div>
                  {addrForm.derasarNotes && (
                    <div className={styles.infoItem} style={{ gridColumn: '1 / -1' }}>
                      <span className={styles.infoLabel}>Admin Notes</span>
                      <span className={styles.infoValue} style={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                        {addrForm.derasarNotes}
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className={styles.infoItem} style={{ gridColumn: '1 / -1' }}>
                    <span className={styles.infoLabel}>Gender</span>
                    <span className={styles.infoValue}>
                      {addrForm.gender ? addrForm.gender.charAt(0).toUpperCase() + addrForm.gender.slice(1) : '—'}
                    </span>
                  </div>
                  <div className={styles.infoItem} style={{ gridColumn: '1 / -1' }}>
                    <span className={styles.infoLabel}>Date of Birth</span>
                    <span className={styles.infoValue}>
                      {addrForm.dateOfBirth ? new Date(addrForm.dateOfBirth + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) : '—'}
                    </span>
                  </div>
                </>
              )}
              <div className={styles.infoItem} style={{ gridColumn: '1 / -1' }}>
                <span className={styles.infoLabel}>Address</span>
                <span className={styles.infoValue} style={{ lineHeight: 1.4 }}>
                  {formatAddress(addrForm)}
                </span>
              </div>
              {user?.role !== 'derasar_admin' && addrForm.notes && (
                <div className={styles.infoItem} style={{ gridColumn: '1 / -1' }}>
                  <span className={styles.infoLabel}>Notes</span>
                  <span className={styles.infoValue} style={{ whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                    {addrForm.notes}
                  </span>
                </div>
              )}
            </div>
          )
        ) : (
          <div style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>Loading…</div>
        )}
      </div>}

      {/* Change password */}
      <div className={styles.profileCard}>
        <div className={styles.sectionTitle}><FiLock size={15} style={{ marginRight: 6 }} />Change Password</div>
        <form onSubmit={handleChangePassword}>
          <div className="form-group">
            <label>Current Password *</label>
            <input name="currentPassword" type="password" value={pwForm.currentPassword} onChange={handlePwChange} required />
          </div>
          <div className="form-group">
            <label>New Password *</label>
            <input name="newPassword" type="password" value={pwForm.newPassword} onChange={handlePwChange} required minLength={6} placeholder="At least 6 characters" />
          </div>
          <div className="form-group">
            <label>Confirm New Password *</label>
            <input name="confirmPassword" type="password" value={pwForm.confirmPassword} onChange={handlePwChange} required minLength={6} />
          </div>
          <button type="submit" className="btn btn-primary" disabled={pwLoading}>
            {pwLoading ? 'Updating…' : 'Update Password'}
          </button>
        </form>
      </div>
      {showMobileVerifyModal && (
        <MobileVerificationModal
          currentPhone={user?.phone || ''}
          onClose={() => setShowMobileVerifyModal(false)}
          onVerified={refreshUser}
        />
      )}
    </div>
  );
};

export default ProfilePage;
